'use client';

import { fetchCustomerAccount } from '@/api/account';
import { Currency, User } from '@/api/types/public';
import Cookie from 'js-cookie';
import { useEffect } from 'react';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { getCurrency } from '@/utils/currency/get-currency';
import { getEnvDefaultCurrency } from '@/utils/currency/util-currency';
import { readAuthTokenFromCookie } from '@/utils/tools';
import useSyncToken from './useSyncToken';

/**
 * 客户端同步 token、拉取账户（替代 layout 中 getUserState + prepareStoreInitialData 用户字段）
 */
export const useClientDataInit = (layoutCurrencyList?: Currency[]) => {
  const setCurrency = useStore((state) => state.setCurrency);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const setWishNum = useStore((state) => state.setWishNum);
  const setCurrencyReady = useStore((state) => state.setCurrencyReady);
  const setUserInfoReady = useStore((state) => state.setUserInfoReady);
  // 设置 currency 到 cookies 的辅助函数
  const setCurrencyToCookie = (currency: Currency) => {
    if (typeof window !== 'undefined' && currency?.name) {
      Cookie.set('zl-currency', currency.name, { expires: 365, sameSite: 'lax', path: '/' });
    }
  };
  useSyncToken();

  useEffect(() => {
    async function fetchUserInfo() {
      if (!readAuthTokenFromCookie()) {
        return setUserInfoReady(true);
      }
      try {
        const res = await fetchCustomerAccount();
        if (!res || res.code !== 200 || !res.data) return setUserInfoReady(true);
        const user = res.data as User;
        setUserInfo(user);
        if (typeof user.wish_num === 'number') {
          setWishNum(user.wish_num);
        }
      } catch (error) {
        logger.error('Failed to fetch customer account on client init:', error);
      } finally {
        setUserInfoReady(true);
      }
    }
    fetchUserInfo();
  }, [setUserInfo, setUserInfoReady, setWishNum]);

  /**
   * TODO: 这里本不需要执行 setStore 的操作
   *      但是 setStore 里会有副作用（localStorage操作），因此这里需要再执行一次，但是不会触发状态变化
   *      建议后续把副作用改在 useEffect 中执行
   *
   * 货币解析：`get-currency` 的 getCurrency（浏览器分支：URL → Cookie → IP → Env）；服务端分支仅 Bot/默认，首屏 SSR 若需完整 header 链路仍看布局里 getServerCurrency
   */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const currencyInfo = await getCurrency();
        if (cancelled || !currencyInfo) return;
        switch (currencyInfo.source) {
          case 'URL':
          case 'IP':
            setCurrency(currencyInfo.currency);
            setCurrencyToCookie(currencyInfo.currency);

            break;
          case 'Cookie':
          case 'Env':
            setCurrency(currencyInfo.currency);
            break;
          default:
            break;
        }
        setCurrencyReady(true);
      } catch (error) {
        logger.error('Failed to resolve client currency:', error);
        if (cancelled) return;
        const fallbackList = layoutCurrencyList?.length ? layoutCurrencyList : [];
        setCurrency(getEnvDefaultCurrency(fallbackList));
        setCurrencyReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [layoutCurrencyList, setCurrency, setCurrencyReady]);
};
